import { UNIDENTIFIED_SPEAKER_ID } from 'src/components/TranscriptViewer/ParagraphAuthor/constants'

import type { Qac } from './crdtV2'
import { Language, LANGUAGES_WITHOUT_WHITESPACES, LanguageWithoutWhitespaces } from './Language'
import { TranscriptOld, TranscriptParagraphOld } from './TranscriptOld'
import { isPunctuation } from './TranscriptWord'

export type TranscriptParagraphContent = {
    text: string
    isEditable: boolean
}

export const TranscriptParagraphContent = {
    EMPTY: {
        text: '',
        isEditable: false,
    } as TranscriptParagraphContent,

    toJson: (content: TranscriptParagraphContent): Json => content,

    fromJson: (json: Json): TranscriptParagraphContent => {
        const content = { ...TranscriptParagraphContent.EMPTY }
        if (!json || typeof json !== 'object' || Array.isArray(json)) {
            return content
        }

        if (typeof json.text === 'string') {
            content.text = json.text
        }
        if (typeof json.isEditable === 'boolean') {
            content.isEditable = json.isEditable
        }

        return content
    },
}

export type TranscriptParagraph = {
    id: string
    speakerId: string
    qac: Qac
    timestampAbsolute: number | null
    timestampRelative: number | null
    startTime: number | null
    endTime: number | null
    content: TranscriptParagraphContent[]
}

export const TranscriptParagraph = {
    EMPTY: {
        id: '',
        speakerId: UNIDENTIFIED_SPEAKER_ID,
        qac: null,
        timestampAbsolute: null,
        timestampRelative: null,
        startTime: null,
        endTime: null,
        content: [],
    } as TranscriptParagraph,

    // temporary, will be redundant after switching to Y.js
    fromOldStructure: ({
        oldParagraph,
        shouldUseWhitespaces,
        isEditable,
    }: {
        oldParagraph: TranscriptParagraphOld
        shouldUseWhitespaces: boolean
        isEditable: boolean
    }): {
        paragraph: TranscriptParagraph
        wordTimings: WordTiming[]
    } => {
        const { id, authorId, time, timeAbsolute, words } = oldParagraph
        const lastWord = words[words.length - 1]
        const paragraph: TranscriptParagraph = {
            id,
            speakerId: authorId,
            qac: null,
            timestampAbsolute: timeAbsolute,
            timestampRelative: time,
            startTime: time,
            endTime: lastWord.time + lastWord.duration,
            content: [],
        }
        const wordTimings: WordTiming[] = []

        let content = ''
        words.forEach(({ text, time: wordTime, duration }, i) => {
            if (shouldUseWhitespaces && i !== 0 && !isPunctuation(text)) {
                content += ' '
            }

            wordTimings.push({
                paragraphId: id,
                mediaStart: wordTime,
                mediaEnd: wordTime + duration,
                charStart: content.length,
                charEnd: content.length + text.length,
            })

            content += text
        })
        paragraph.content.push({
            text: content,
            isEditable,
        })

        return {
            paragraph,
            wordTimings,
        }
    },

    textOf: (paragraph: TranscriptParagraph): string => paragraph.content.map(({ text }) => text).join(''),

    toJson: (paragraph: TranscriptParagraph): Json => paragraph,

    fromJson: (json: Json): TranscriptParagraph => {
        const paragraph = { ...TranscriptParagraph.EMPTY }
        if (!json || typeof json !== 'object' || Array.isArray(json)) {
            return paragraph
        }

        if (typeof json.id === 'string') {
            paragraph.id = json.id
        }
        if (typeof json.speakerId === 'string') {
            paragraph.speakerId = json.speakerId
        }
        if (json.qac === 'Q' || json.qac === 'A') {
            paragraph.qac = json.qac
        }
        if (typeof json.timestampAbsolute === 'number') {
            paragraph.timestampAbsolute = json.timestampAbsolute
        }
        if (typeof json.timestampRelative === 'number') {
            paragraph.timestampRelative = json.timestampRelative
        }
        if (typeof json.startTime === 'number') {
            paragraph.startTime = json.startTime
        }
        if (typeof json.endTime === 'number') {
            paragraph.endTime = json.endTime
        }
        if (Array.isArray(json.content)) {
            paragraph.content = json.content.map((entry) => TranscriptParagraphContent.fromJson(entry))
        }

        return paragraph
    },
}

export type TranscriptSpeaker = {
    id: string
    name: string
}

export type Select2SpeakerPresentation = {
    id: string | null
    name: string
    role?: string
    color?: string
}

export type WordTiming = {
    mediaStart: number
    mediaEnd: number
    charStart: number
    charEnd: number
    paragraphId: string
}
export type TranscriptTimings = {
    [paragraphId: string]: WordTiming[]
}

export type Transcript = {
    paragraphIds: string[]
    paragraphs: Record<string, TranscriptParagraph>
    speakers: Record<string, TranscriptSpeaker>
    recordingStartedAt: number
    timings: TranscriptTimings
}

// Covers insight offsets floored to whole seconds, which can land up to 1s before the paragraph they name.
// Only whole-second offsets get it, so a precise offset in a paragraph's last second never snaps forward.
const FLOORED_OFFSET_TOLERANCE_SECONDS = 1

export const Transcript = {
    EMPTY: {
        paragraphIds: [],
        paragraphs: {},
        speakers: {},
        recordingStartedAt: 0,
        timings: {},
    } as Transcript,

    getParagraphContent: ({ paragraphs }: Transcript, paragraphId: string) => paragraphs[paragraphId].content,

    getTotalParagraphCount: ({ paragraphIds }: Transcript) => paragraphIds.length,
    getParagraphByIndex: ({ paragraphIds, paragraphs }: Transcript, index: number) => {
        const id = paragraphIds[index]

        return paragraphs[id]
    },

    getParagraphById: ({ paragraphs }: Transcript, id: string) => paragraphs[id],

    getParagraphIndexById: ({ paragraphIds }: Transcript, paragraphId: string) => paragraphIds.findIndex((id) => id === paragraphId),

    getParagraphIndexByTime: ({ paragraphIds, paragraphs }: Transcript, timeToFind: number) => {
        if (!paragraphIds.length) {
            return -1
        }

        let closestIndex = 0
        let closestDistance = Number.POSITIVE_INFINITY

        for (let index = 0; index < paragraphIds.length; index++) {
            const paragraph = paragraphs[paragraphIds[index]]
            if (!paragraph) {
                continue
            }

            const startTime = paragraph.startTime
            const endTime = paragraph.endTime

            if (startTime !== null && endTime !== null && timeToFind >= startTime && timeToFind < endTime) {
                return index
            }

            let distance = Number.POSITIVE_INFINITY
            if (startTime !== null && endTime !== null) {
                if (timeToFind < startTime) {
                    distance = startTime - timeToFind
                } else if (timeToFind > endTime) {
                    distance = timeToFind - endTime
                } else {
                    distance = 0
                }
            } else if (paragraph.timestampRelative !== null) {
                distance = Math.abs(timeToFind - paragraph.timestampRelative)
            }

            if (distance <= closestDistance) {
                closestDistance = distance
                closestIndex = index
            }
        }

        if (closestDistance === Number.POSITIVE_INFINITY) {
            return -1
        }

        return closestIndex
    },

    /**
     * Finds the paragraph an insight offset (chapter start, inconsistency anchor, etc.) belongs to:
     * the one with the latest start at or before `timeToFind`.
     *
     * Insight offsets are not always paragraph starts — an inconsistency anchors on the backend's own
     * utterance, which can sit late in a long paragraph — so nearest-start would jump to the next
     * paragraph (LG-2403). A whole-second offset may be floored and land just before the paragraph it
     * names, so it gets `FLOORED_OFFSET_TOLERANCE_SECONDS` of forward tolerance; fractional offsets are
     * precise and get none. An offset before every paragraph resolves to the earliest one.
     *
     * For arbitrary positions within the transcript (e.g. playback cursor), use
     * `getParagraphIndexByTime` instead, which respects paragraph end times.
     */
    getParagraphIndexByInsightOffset: ({ paragraphIds, paragraphs }: Transcript, timeToFind: number) => {
        const target = Number.isInteger(timeToFind) ? timeToFind + FLOORED_OFFSET_TOLERANCE_SECONDS : timeToFind

        let containingIndex = -1
        let containingStart = Number.NEGATIVE_INFINITY
        let earliestIndex = -1
        let earliestStart = Number.POSITIVE_INFINITY

        for (let index = 0; index < paragraphIds.length; index++) {
            const paragraph = paragraphs[paragraphIds[index]]
            if (!paragraph) {
                continue
            }

            const anchorTime = paragraph.startTime ?? paragraph.timestampRelative
            if (anchorTime === null) {
                continue
            }

            if (anchorTime <= target && anchorTime > containingStart) {
                containingStart = anchorTime
                containingIndex = index
            }
            if (anchorTime < earliestStart) {
                earliestStart = anchorTime
                earliestIndex = index
            }
        }

        return containingIndex !== -1 ? containingIndex : earliestIndex
    },

    getSpeakers: ({ speakers }: Transcript) => Object.values(speakers),
    getSpeakerById: ({ speakers }: Transcript, id: string) => speakers[id],

    isDiarizationSupported: ({ speakers }: Transcript) => Object.values(speakers).findIndex(({ name }) => !!name) !== -1,

    fromOldStructure: (transcript: TranscriptOld, language?: Language): Transcript => {
        const {
            paragraphs: oldParagraphs,
            asrParagraphs,
            authors: speakers,
            recordingStartedAt: oldRecordingStartDate,
            unfinishedUtteranceId,
        } = transcript
        const shouldUseWhitespaces = !LANGUAGES_WITHOUT_WHITESPACES.includes(language as LanguageWithoutWhitespaces)

        const paragraphIds: string[] = []
        const paragraphs: Record<string, TranscriptParagraph> = {}
        const timings: TranscriptTimings = {}

        oldParagraphs.forEach((p) => {
            paragraphIds.push(p.id)
            const { paragraph, wordTimings } = TranscriptParagraph.fromOldStructure({
                oldParagraph: p,
                shouldUseWhitespaces,
                isEditable: true,
            })
            paragraphs[p.id] = paragraph
            timings[p.id] = wordTimings
        })
        asrParagraphs.forEach((p) => {
            paragraphIds.push(p.id)
            const { paragraph, wordTimings } = TranscriptParagraph.fromOldStructure({
                oldParagraph: p,
                shouldUseWhitespaces,
                isEditable: !p.uttIds.includes(unfinishedUtteranceId as number),
            })
            paragraphs[p.id] = paragraph
            timings[p.id] = wordTimings
        })

        const recordingStartedAt = oldRecordingStartDate.getTime() / 1000

        return {
            paragraphIds,
            paragraphs,
            speakers,
            recordingStartedAt,
            timings,
        }
    },
}
